(ns ui.status
  "Everything that paints a run status — pill, bar, legend — so a bar and the
   pill on its row cannot disagree. The vocabulary they key off is
   `ui.status-vocab`."
  (:require
   [ui.chip :as chip]
   [ui.utils :refer [clsx]]))


(def status-pill-styles
  {:failed    ["Failed"  :error]
   :succeeded ["Success" :success]
   :running   ["Running" :warning]
   :queued    ["Queued"  :secondary]
   :skipped   ["Skipped" :secondary]
   :other     ["—"       :secondary-border]})


(defn status-pill [status & [{:keys [class]}]]
  (let [[label variant] (get status-pill-styles status (:other status-pill-styles))]
    [chip/chip {:class (or class "shrink-0") :variant variant} label]))


(def status-text
  "Spoken name of a status, for tooltips and screen readers — `:other` gets a word
   rather than the pill's dash."
  {:failed    "Failed"
   :succeeded "Success"
   :running   "Running"
   :queued    "Queued"
   :skipped   "Skipped"
   :other     "Unknown"})

(defn status-label [status]
  (get status-text status (:other status-text)))


;; Colour is never the only channel (WCAG 1.4.1): running is striped,
;; queued/skipped hatched.
(def ^:private stripes
  "repeating-linear-gradient(115deg, rgba(255,255,255,.55) 0 2px, transparent 2px 6px)")

(def ^:private hatch
  "repeating-linear-gradient(135deg, rgba(255,255,255,.5) 0 1px, transparent 1px 4px)")

(def status-bar-styles
  {:failed    {:class "bg-kleene-text-error"}
   :succeeded {:class "bg-kleene-text-success"}
   :running   {:class "bg-kleene-text-warning" :style {:background-image stripes}}
   :queued    {:class "bg-kleene-text-secondary/50" :style {:background-image hatch}}
   :skipped   {:class "bg-kleene-text-secondary/30" :style {:background-image hatch}}
   :other     {:class "bg-kleene-text-secondary/70"}})

(defn status-bar
  "`{:class :style}` for a gantt bar of `status` (a `normalize-status` keyword)."
  [status]
  (get status-bar-styles status (:other status-bar-styles)))

;; A gantt schedule tick: its line and its time chip. Lighter than the bars, as a
;; tick crosses them.
(def status-tick-styles
  {:succeeded {:line "bg-kleene-text-success/50 group-hover:bg-kleene-text-success"
               :chip "bg-kleene-text-success/15 text-kleene-text-success"}
   :failed    {:line "bg-kleene-text-error/60 group-hover:bg-kleene-text-error"
               :chip "bg-kleene-text-error/15 text-kleene-text-error"}
   :running   {:line "bg-kleene-text-warning/60 group-hover:bg-kleene-text-warning"
               :chip "bg-kleene-text-warning/15 text-kleene-text-warning"}
   :other     {:line "bg-kleene-text-secondary/50 group-hover:bg-kleene-text-secondary"
               :chip "bg-kleene-text-secondary/15 text-kleene-text-secondary"}})

(defn status-tick
  "`{:line :chip}` classes for a schedule tick of `status`."
  [status]
  (get status-tick-styles status (:other status-tick-styles)))


;; `:queued` is absent deliberately: a queued run has a fire-time but no start,
;; and the gantt draws nothing without one — so the legend would advertise a
;; colour that cannot appear beside it.
(def legend-statuses [:succeeded :failed :running :skipped])

(defn status-legend
  "Key for the gantt's bar colours."
  [& [{:keys [class statuses]}]]
  [:div {:class (clsx "flex items-center gap-3 text-xs text-kleene-text-secondary" class)}
   (for [s (or statuses legend-statuses)
         :let [{bar-class :class bar-style :style} (status-bar s)]]
     ^{:key (name s)}
     [:div {:class "flex items-center gap-1.5"}
      [:span {:class (clsx "h-2 w-4 rounded-sm shrink-0" bar-class)
              :style bar-style}]
      [:span (status-label s)]])])
